import { Trash2, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import type { BulkDeleteState } from './use-bulk-delete';

/**
 * The "N selected · Delete · Clear" strip that stands in for a list's toolbar
 * while rows are selected. Pass it as `ListPage`'s `toolbar` when
 * `bulk.hasSelection`, and the page's normal controls otherwise.
 *
 * `variant="mobile"` is the phone layout for `MobilePageShell` pages: a
 * full-width row with the count on the left and pill-shaped actions on the
 * right, matching the filter chips above it. The desktop strip cannot be
 * reused there — it right-aligns and never wraps, so on a 375px screen the
 * count was pushed off the left edge and the buttons sat jammed together.
 */
export function BulkSelectionToolbar({
  bulk,
  actions,
  variant = 'desktop',
  className,
}: {
  bulk: Pick<BulkDeleteState, 'count' | 'isDeleting' | 'openDialog' | 'clear'>;
  /**
   * Further bulk actions for the selected rows — "Assign to team" on the
   * services page. Rendered BEFORE Delete, so the strip keeps reading as
   * "what you can do with these" and ends on the one destructive option.
   * Keep them non-destructive: the red button is Delete, and only Delete.
   */
  actions?: ReactNode;
  variant?: 'desktop' | 'mobile';
  className?: string;
}) {
  if (variant === 'mobile') {
    return (
      <div
        className={cn(
          'flex w-full min-w-0 items-center justify-between gap-2',
          className
        )}
      >
        <span className="truncate text-sm font-semibold text-[#0A0A0A]">
          {bulk.count} selected
        </span>
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          <Button
            className="h-9 rounded-full px-3"
            disabled={bulk.isDeleting}
            onClick={bulk.openDialog}
            size="sm"
            variant="destructive"
          >
            <Trash2 className="size-4" />
            {bulk.isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
          {/* Icon-only: "Clear" spelled out is the one word that does not fit
              beside Assign and Delete on a phone, and the X reads as "dismiss
              this selection" next to the count. */}
          <Button
            aria-label="Clear selection"
            className="size-9 rounded-full border-[#E5E5EA]"
            disabled={bulk.isDeleting}
            onClick={bulk.clear}
            size="icon"
            variant="outline"
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    // `flex-1 justify-end` pushes the strip against the primary action, where
    // the page's own toolbar controls were. The strip itself never wraps: at
    // narrow widths it moves as one unit onto its own row instead of splitting
    // "N selected", Delete and Clear across three lines.
    <div
      className={cn(
        'flex shrink-0 flex-1 flex-nowrap items-center justify-end gap-2',
        className
      )}
    >
      <span className="text-muted-foreground whitespace-nowrap text-sm">
        {bulk.count} selected
      </span>
      {actions}
      <Button
        disabled={bulk.isDeleting}
        onClick={bulk.openDialog}
        size="sm"
        variant="destructive"
      >
        <Trash2 className="size-4" />
        {bulk.isDeleting ? 'Deleting…' : 'Delete'}
      </Button>
      <Button
        disabled={bulk.isDeleting}
        onClick={bulk.clear}
        size="sm"
        variant="ghost"
      >
        <X className="size-4" />
        Clear
      </Button>
    </div>
  );
}
